/**
 * Shipping Rate Markup Configuration
 *
 * This file contains the configuration for TradMarket's shipping rate markup system.
 *
 * How it works:
 * 1. We get a discounted rate (baseCost) from EasyPost — this is our actual cost
 * 2. We apply a percentage markup to baseCost, with a minimum margin floor
 * 3. We split the margin between TradMarket and the seller
 *
 * Example with baseCost=$13.71 (UPS Ground domestic):
 * - Markup: $13.71 * 10% = $1.37 → below $3.00 floor, so margin = $3.00
 * - Buyer Rate: $13.71 + $3.00 = $16.71
 * - Seller Discount: $3.00 * 50% = $1.50
 * - Seller Rate: $16.71 - $1.50 = $15.21
 * - TradMarket Profit: $1.50
 *
 * Example with baseCost=$243.57 (UPS Ground guitar box to Hawaii):
 * - Markup: $243.57 * 10% = $24.36 → above $3.00 floor, so margin = $24.36
 * - Buyer Rate: $243.57 + $24.36 = $267.93
 * - Seller Discount: $24.36 * 50% = $12.18
 * - Seller Rate: $267.93 - $12.18 = $255.75
 * - TradMarket Profit: $12.18
 *
 * TO ADJUST MARGINS:
 * - Change DOMESTIC_MARKUP_PERCENT / INTERNATIONAL_MARKUP_PERCENT to adjust buyer pricing
 * - Change DOMESTIC_MINIMUM_MARGIN / INTERNATIONAL_MINIMUM_MARGIN to adjust minimum floors
 * - Change SELLER_DISCOUNT_SHARE to adjust how much margin goes to seller vs TradMarket
 *
 * PREVIOUS CALCULATION METHOD (before March 2026):
 * - Based buyer rate on retail_rate with a percentage discount (BUYER_RATE_ADJUSTMENT)
 * - BUYER_RATE_ADJUSTMENT = -50 (buyer paid 50% of retail rate)
 * - Problem: International retail rates were 5-6x EasyPost cost,
 *   producing buyer prices ~3x actual cost (e.g., $174 on a $64.90 shipment)
 * - See git history for the original implementation
 */

/**
 * DOMESTIC MARKUP PERCENT
 *
 * Percentage markup applied to our EasyPost cost for domestic (same-country) shipments.
 * The actual margin will be the greater of this percentage or the minimum margin floor.
 *
 * Example: 10 means buyer pays baseCost + 10% of baseCost (if above the floor)
 */
export const DOMESTIC_MARKUP_PERCENT = 10;

/**
 * DOMESTIC MINIMUM MARGIN
 *
 * Minimum dollar margin for domestic shipments. If the percentage markup produces
 * a margin below this amount, this floor is used instead.
 *
 * Example: 3.00 means TradMarket + seller will always split at least $3.00
 */
export const DOMESTIC_MINIMUM_MARGIN = 3.00;

/**
 * INTERNATIONAL MARKUP PERCENT
 *
 * Percentage markup applied to our EasyPost cost for international (cross-border) shipments.
 * The actual margin will be the greater of this percentage or the minimum margin floor.
 *
 * Example: 10 means buyer pays baseCost + 10% of baseCost (if above the floor)
 */
export const INTERNATIONAL_MARKUP_PERCENT = 10;

/**
 * INTERNATIONAL MINIMUM MARGIN
 *
 * Minimum dollar margin for international shipments. If the percentage markup produces
 * a margin below this amount, this floor is used instead.
 *
 * Example: 6.00 means TradMarket + seller will always split at least $6.00
 */
export const INTERNATIONAL_MINIMUM_MARGIN = 6.00;

/**
 * SELLER DISCOUNT SHARE
 *
 * Percentage of the total margin that goes to the seller as a discount.
 * The remaining percentage is TradMarket's profit.
 *
 * - 0 = seller gets no discount, TradMarket keeps all margin
 * - 50 = seller gets 50% of margin as discount, TradMarket keeps 50%
 * - 100 = seller gets all margin as discount, TradMarket profit is 0
 *
 * Default: 50 (50/50 split between seller and TradMarket)
 */
export const SELLER_DISCOUNT_SHARE = 50;

/**
 * PER-RATE MARKUP OVERRIDES
 *
 * Override the global DOMESTIC/INTERNATIONAL markup + minimum margin for
 * specific carriers (or specific carrier+service combinations) where the
 * defaults produce a margin we want to tune. Anything not listed here
 * falls back to the globals above.
 *
 * Key formats — looked up most specific first:
 *   1. `${carrier}:${service}` — exact carrier + service combo
 *   2. `${carrier}`             — applies to all services for that carrier
 *   3. (no match)               — global fallback
 *
 * Carriers come from EasyPost's `rate.carrier` (e.g. 'USPS', 'FedEx',
 * 'UPS', 'CanadaPost', 'DHLExpress'). Services come from `rate.service`.
 *
 * Each override may set markupPercent, minimumMargin, or both. Anything
 * omitted falls back to the global. This is the only thing that needs
 * editing when ops wants to retune a single carrier's pricing — no
 * lookup-logic change required.
 *
 * Example:
 *   domestic: {
 *     'USPS': { markupPercent: 12 },                // bump USPS only
 *     'UPS:Ground': { minimumMargin: 4.50 },        // higher floor for UPS Ground
 *   },
 */
export const MARKUP_OVERRIDES = {
  domestic: {},
  international: {},
};

/**
 * Resolve the markupPercent + minimumMargin to apply for a given rate.
 * Looks up overrides by 'carrier:service' first, then by 'carrier'
 * alone, then falls back to the global. Each field falls back
 * independently — an override that sets only markupPercent still gets
 * the global minimumMargin, and vice versa.
 */
const resolveRateMarkup = (rate, isDomestic) => {
  const globalMarkup = isDomestic ? DOMESTIC_MARKUP_PERCENT : INTERNATIONAL_MARKUP_PERCENT;
  const globalFloor = isDomestic ? DOMESTIC_MINIMUM_MARGIN : INTERNATIONAL_MINIMUM_MARGIN;

  const carrier = rate?.carrier || '';
  const service = rate?.service || '';
  const overrides = (isDomestic ? MARKUP_OVERRIDES.domestic : MARKUP_OVERRIDES.international) || {};
  const exact = carrier && service ? overrides[`${carrier}:${service}`] : null;
  const carrierWide = carrier ? overrides[carrier] : null;

  const markupPercent =
    (exact && exact.markupPercent != null ? exact.markupPercent : null) ??
    (carrierWide && carrierWide.markupPercent != null ? carrierWide.markupPercent : null) ??
    globalMarkup;
  const minimumMargin =
    (exact && exact.minimumMargin != null ? exact.minimumMargin : null) ??
    (carrierWide && carrierWide.minimumMargin != null ? carrierWide.minimumMargin : null) ??
    globalFloor;

  return { markupPercent, minimumMargin };
};

/**
 * Calculate the rate that the buyer will see and pay
 *
 * @param {Object} rate - The rate object from EasyPost
 * @param {number} rate.rate - Our discounted cost from EasyPost (base cost)
 * @param {boolean} isDomestic - Whether this is a domestic (same-country) shipment
 * @returns {number} The calculated buyer rate in dollars
 */
export const calculateBuyerRate = (rate, isDomestic = true) => {
  const baseCost = parseFloat(rate.rate) || 0;
  if (baseCost <= 0) return 0;

  // Per-rate override (if any), falling back to the global domestic /
  // international values for fields the override doesn't set.
  const { markupPercent, minimumMargin } = resolveRateMarkup(rate, isDomestic);

  // Calculate margin: percentage markup or minimum floor, whichever is greater
  const percentageMargin = baseCost * (markupPercent / 100);
  const margin = Math.max(percentageMargin, minimumMargin);

  const buyerRate = baseCost + margin;

  return buyerRate;
};

/**
 * Calculate the rate that the seller will see and pay
 *
 * @param {Object} rate - The rate object from EasyPost
 * @param {number} rate.rate - Our discounted cost from EasyPost (base cost)
 * @param {boolean} isDomestic - Whether this is a domestic (same-country) shipment
 * @returns {number} The calculated seller rate in dollars
 */
export const calculateSellerRate = (rate, isDomestic = true) => {
  // Get base cost (what we pay EasyPost)
  const baseCost = parseFloat(rate.rate) || 0;

  // Calculate buyer rate first
  const buyerRate = calculateBuyerRate(rate, isDomestic);

  // Calculate total margin
  const totalMargin = buyerRate - baseCost;

  // Calculate seller's discount (their share of the margin)
  const sellerDiscount = totalMargin * (SELLER_DISCOUNT_SHARE / 100);

  // Seller pays buyer rate minus their discount
  const sellerRate = buyerRate - sellerDiscount;

  return sellerRate;
};

/**
 * Detects whether a shipping address is a PO Box.
 * UPS and FedEx cannot deliver to PO Boxes — only USPS can.
 */
const PO_BOX_REGEX = /\b(p\.?\s*o\.?\s*b(ox)?\.?|post\s+office\s+box)\b/i;
export const isPOBoxAddress = (line1 = '', line2 = '') =>
  PO_BOX_REGEX.test(line1 || '') || PO_BOX_REGEX.test(line2 || '');
